import { inject } from '@angular/core';
import { CanActivateFn, Router, CanMatchFn, Route, UrlSegment } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { map, take } from 'rxjs/operators';

// Basic auth guard - checks if user is authenticated
export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.isAuthenticated$.pipe(
    take(1),
    map(isAuthenticated => {
      if (isAuthenticated) {
        return true;
      }
      
      // Store the attempted URL for redirecting
      if(state.url == "/"){
        return router.createUrlTree(['/login'], { });
      }
      const returnUrl = state.url;
      return router.createUrlTree(['/login'], { queryParams: { returnUrl } });
    })
  );
};

// Guest guard - redirects authenticated users away from auth pages
export const guestGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.isAuthenticated$.pipe(
    take(1),
    map(isAuthenticated => {
      if (!isAuthenticated) {
        return true;
      }
      
      // Redirect to agents page or dashboard if already logged in
      return router.createUrlTree(['/agents']);
    })
  );
};

// Role-based guard - checks if user has required role
export const roleGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const requiredRole = route.data['requiredRole'];

  return authService.currentUser$.pipe(
    take(1),
    map(user => {
      // First check if the user is authenticated
      if (!user) {
        // Store the attempted URL for redirecting
        if(state.url == "/"){
          return router.createUrlTree(['/login'], { });
        }
        const returnUrl = state.url;
        return router.createUrlTree(['/login'], { queryParams: { returnUrl } });
      }
      
      // Then check if user has the required role
      if ((user.role || '').toLowerCase() === (requiredRole || '').toLowerCase() || !requiredRole) {
        return true;
      }
      
      // Redirect to unauthorized page if user doesn't have the required role
      return router.createUrlTree(['/unauthorized']);
    })
  );
};

// Can match guard for lazy loaded routes
export const authCanMatch: CanMatchFn = (route: Route, segments: UrlSegment[]) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.isAuthenticated$.pipe(
    take(1),
    map(isAuthenticated => {
      if (isAuthenticated) {
        return true;
      }

      // Store current attempt path
      const returnUrl = '/' + segments.map(s => s.path).join('/');
      return router.createUrlTree(['/login'], { queryParams: { returnUrl } });
    })
  );
};

// Superuser guard - checks if user is a superuser
export const superuserGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.currentUser$.pipe(
    take(1),
    map(user => {
      // First check if the user is authenticated
      if (!user) {
        const returnUrl = state.url;
        return router.createUrlTree(['/login'], { queryParams: { returnUrl } });
      }

      // Check if user is a superuser
      if (authService.isSuperuser) {
        return true;
      }

      // Redirect to agents page if user is not a superuser
      return router.createUrlTree(['/agents']);
    })
  );
};

// Owner or superuser guard - checks if user can manage organization settings
export const ownerOrSuperuserGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.currentUser$.pipe(
    take(1),
    map(user => {
      if (!user) {
        const returnUrl = state.url;
        return router.createUrlTree(['/login'], { queryParams: { returnUrl } });
      }

      if (authService.isOwner || authService.isSuperuser) {
        return true;
      }

      return router.createUrlTree(['/agents']);
    })
  );
};

// Organization guard - blocks readers from direct admin URL access.
export const organizationGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const user = authService.getCurrentUser();

  if (!user) {
    const returnUrl = state.url;
    return router.createUrlTree(['/login'], { queryParams: { returnUrl } });
  }

  if (authService.isOwner || authService.isSuperuser) {
    return true;
  }

  return router.createUrlTree(['/account']);
};

export const organizationCanMatch: CanMatchFn = (route: Route, segments: UrlSegment[]) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const user = authService.getCurrentUser();

  if (!user) {
    const returnUrl = '/' + segments.map(s => s.path).join('/');
    return router.createUrlTree(['/login'], { queryParams: { returnUrl } });
  }

  if (authService.isOwner || authService.isSuperuser) {
    return true;
  }

  return router.createUrlTree(['/account']);
};

// Strict owner guard - superusers are intentionally excluded.
export const platformSettingsOwnerGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.currentUser$.pipe(
    take(1),
    map(user => {
      if (!user) {
        const returnUrl = state.url;
        return router.createUrlTree(['/login'], { queryParams: { returnUrl } });
      }

      if (authService.canManagePlatformSettings) {
        return true;
      }

      return router.createUrlTree(['/agents']);
    })
  );
};